import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Upload, RefreshCw, Package, IndianRupee, CheckCircle, AlertCircle, Trash2 } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const DTDCManagement = () => {
  const [loading, setLoading] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [errorLogs, setErrorLogs] = useState([]);

  useEffect(() => {
    fetchErrorLogs();
  }, []);

  const handleSyncTracking = async () => {
    try {
      setLoading(true);
      setSyncResult(null);
      
      const response = await axios.post(`${API}/api/dtdc/sync`);
      
      if (response.data.success) {
        setSyncResult(response.data);
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error syncing DTDC:', error);
      toast.error('Failed to sync DTDC tracking');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async () => {
    if (!uploadFile) {
      toast.error('Please select a CSV file');
      return;
    }

    try {
      setLoading(true);
      setUploadResult(null);

      const formData = new FormData();
      formData.append('file', uploadFile);

      const response = await axios.post(`${API}/api/dtdc/import-cod-remittance`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setUploadResult(response.data);
        toast.success(response.data.message);
        setUploadFile(null);
        fetchErrorLogs(); // Refresh error logs
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to import COD remittance file');
    } finally {
      setLoading(false);
    }
  };

  const handleInvoiceUpload = async (file) => {
    try {
      setLoading(true);

      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/api/dtdc/import-invoice`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        toast.success(response.data.message);
        toast.info(`Invoice: ${response.data.invoice_number} | Total: ₹${response.data.total_charges}`);
        
        if (response.data.errors && response.data.errors.length > 0) {
          console.log('Import errors:', response.data.errors);
        }
      }
    } catch (error) {
      console.error('Error uploading invoice:', error);
      toast.error('Failed to import DTDC invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleBankStatementUpload = async (file) => {
    try {
      setLoading(true);

      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/api/dtdc/import-bank-statement`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        toast.success(response.data.message);
        toast.info(`Matched ${response.data.matched_orders} orders`);
        
        if (response.data.errors && response.data.errors.length > 0) {
          setErrorLogs(response.data.errors);
          toast.warning(`${response.data.errors.length} unmatched records - check error log`);
        }
      }
    } catch (error) {
      console.error('Error uploading bank statement:', error);
      toast.error('Failed to import bank statement');
    } finally {
      setLoading(false);
    }
  };

  const handleCostSheetUpload = async (file) => {
    try {
      setLoading(true);

      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/api/dtdc/import-cost-sheet`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        toast.success(response.data.message);
        toast.info(`Updated costs for ${response.data.imported_count} orders`);
        
        if (response.data.errors && response.data.errors.length > 0) {
          setErrorLogs(response.data.errors);
          toast.warning(`${response.data.errors.length} unmatched orders - check error log`);
        }
        
        // Fetch full error logs from database
        fetchErrorLogs();
      }
    } catch (error) {
      console.error('Error uploading cost sheet:', error);
      toast.error('Failed to import cost sheet');
    } finally {
      setLoading(false);
    }
  };

  const fetchErrorLogs = async () => {
    try {
      const response = await axios.get(`${API}/api/import-errors?limit=50`);
      if (response.data.success) {
        setErrorLogs(response.data.errors);
      }
    } catch (error) {
      console.error('Error fetching error logs:', error);
    }
  };

  const clearErrorLogs = async () => {
    try {
      const response = await axios.delete(`${API}/api/import-errors`);
      if (response.data.success) {
        toast.success(response.data.message);
        setErrorLogs([]);
      }
    } catch (error) {
      console.error('Error clearing error logs:', error);
      toast.error('Failed to clear error logs');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">📦 DTDC Management (Ashmiaa)</h1>
          <p className="text-gray-600">Sync tracking status and import COD remittance data</p>
        </div>

        {/* Sync Tracking Card */}
        <Card className="mb-6 border-blue-200 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-blue-100">
            <CardTitle className="flex items-center gap-2">
              <Package className="w-6 h-6 text-blue-600" />
              DTDC Tracking Sync
            </CardTitle>
            <CardDescription>
              Sync delivery status for all Ashmiaa orders with DTDC tracking numbers (I76854xxx)
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <Button
              onClick={handleSyncTracking}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Sync DTDC Tracking
            </Button>

            {syncResult && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="font-semibold text-green-900">Sync Complete!</span>
                </div>
                <p className="text-sm text-green-800 mb-2">
                  Synced {syncResult.synced_count} tracking numbers
                </p>
                {syncResult.updated_orders && syncResult.updated_orders.length > 0 && (
                  <div className="space-y-1">
                    {syncResult.updated_orders.map((order, idx) => (
                      <div key={idx} className="text-xs text-green-700 font-mono">
                        #{order.order_number}: {order.old_status} → {order.new_status}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* DTDC Invoice Upload Card */}
        <Card className="mb-6 border-orange-200 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-orange-50 to-orange-100">
            <CardTitle className="flex items-center gap-2">
              <Package className="w-6 h-6 text-orange-600" />
              DTDC Invoice Import (Shipping Charges)
            </CardTitle>
            <CardDescription>
              Upload DTDC invoice PDF to import shipping charges per tracking number
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select DTDC Invoice PDF
                </label>
                <Input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      handleInvoiceUpload(file);
                    }
                  }}
                  className="cursor-pointer"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* COD Remittance Upload Card */}
        <Card className="mb-6 border-green-200 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-green-50 to-green-100">
            <CardTitle className="flex items-center gap-2">
              <IndianRupee className="w-6 h-6 text-green-600" />
              COD Remittance Import
            </CardTitle>
            <CardDescription>
              Upload DTDC COD remittance CSV file to sync payment data
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select COD Remittance CSV File
                </label>
                <Input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                  className="cursor-pointer"
                />
                {uploadFile && (
                  <p className="text-sm text-gray-600 mt-2">
                    Selected: <span className="font-medium">{uploadFile.name}</span>
                  </p>
                )}
              </div>

              <Button
                onClick={handleFileUpload}
                disabled={loading || !uploadFile}
                className="bg-green-600 hover:bg-green-700"
              >
                <Upload className="w-4 h-4 mr-2" />
                Import COD Remittance
              </Button>

              {uploadResult && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="font-semibold text-green-900">Import Complete!</span>
                  </div>
                  <div className="grid grid-cols-3 gap-4 mt-3">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">{uploadResult.imported_count}</p>
                      <p className="text-xs text-gray-600">Imported</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gray-600">{uploadResult.skipped_count}</p>
                      <p className="text-xs text-gray-600">Skipped</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-600">{uploadResult.total_rows}</p>
                      <p className="text-xs text-gray-600">Total Rows</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Cost Sheet Upload Card */}
        <Card className="mt-6 border-indigo-200 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-indigo-50 to-indigo-100">
            <CardTitle className="flex items-center gap-2">
              <Package className="w-6 h-6 text-indigo-600" />
              Cost Sheet Import
            </CardTitle>
            <CardDescription>
              Upload cost sheet CSV/Excel to update product costs per order
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Cost Sheet File (CSV/Excel)
                </label>
                <Input
                  type="file"
                  accept=".csv,.xls,.xlsx"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      handleCostSheetUpload(file);
                    }
                  }}
                  className="cursor-pointer"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-900">
                  <strong>Expected columns:</strong>
                </p>
                <ul className="text-xs text-blue-800 mt-2 space-y-1 ml-4 list-disc">
                  <li>Order Number (e.g., 4151) OR Tracking Number</li>
                  <li>Cost (e.g., 1500.00)</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bank Statement Upload Card */}
        <Card className="mt-6 border-purple-200 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-purple-50 to-purple-100">
            <CardTitle className="flex items-center gap-2">
              <IndianRupee className="w-6 h-6 text-purple-600" />
              Bank Statement Import
            </CardTitle>
            <CardDescription>
              Upload bank statement to match COD receipts and shipping charges
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Bank Statement File (CSV/Excel)
                </label>
                <Input
                  type="file"
                  accept=".csv,.xls,.xlsx"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      handleBankStatementUpload(file);
                    }
                  }}
                  className="cursor-pointer"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-900">
                  <strong>This will automatically:</strong>
                </p>
                <ul className="text-xs text-blue-800 mt-2 space-y-1 ml-4 list-disc">
                  <li>Match COD receipts using UTR/AXISCN numbers</li>
                  <li>Match shipping charges to orders</li>
                  <li>Calculate net profit per order</li>
                  <li>Flag mismatches for review</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Error Log Section */}
        {errorLogs.length > 0 && (
          <Card className="mt-6 border-red-200 bg-red-50">
            <CardHeader>
              <CardTitle className="text-lg text-red-900 flex items-center gap-2">
                ⚠️ Unmatched Records ({errorLogs.length})
              </CardTitle>
              <CardDescription>
                These records couldn't be matched to existing orders
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="max-h-60 overflow-y-auto space-y-1">
                {errorLogs.map((error, idx) => (
                  <div key={idx} className="text-sm text-red-800 font-mono bg-white p-2 rounded">
                    {error}
                  </div>
                ))}
              </div>
              <Button
                onClick={() => setErrorLogs([])}
                variant="outline"
                size="sm"
                className="mt-3"
              >
                Clear Error Log
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Error Logs Section */}
        {errorLogs.length > 0 && (
          <Card className="mt-6 border-orange-200">
            <CardHeader className="bg-gradient-to-r from-orange-50 to-orange-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-orange-600" />
                  <CardTitle>Import Error Logs</CardTitle>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearErrorLogs}
                  className="border-red-300 text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear All
                </Button>
              </div>
              <CardDescription>
                Records that couldn't be matched during import. Review and fix these orders manually.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold">Type</TableHead>
                      <TableHead className="font-semibold">Order/Tracking</TableHead>
                      <TableHead className="font-semibold">Error Message</TableHead>
                      <TableHead className="font-semibold">Timestamp</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {errorLogs.map((error, index) => (
                      <TableRow key={index} className="hover:bg-gray-50">
                        <TableCell>
                          <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                            {error.import_type || 'unknown'}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {error.order_ref || error.order_number || error.tracking_number || '—'}
                        </TableCell>
                        <TableCell className="text-sm text-gray-700">
                          {error.error_message}
                        </TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {error.timestamp ? new Date(error.timestamp).toLocaleString() : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Help Section */}
        <Card className="mt-6 border-gray-200">
          <CardHeader>
            <CardTitle className="text-lg">ℹ️ How to Use</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <p className="font-semibold mb-1">1. DTDC Tracking Sync:</p>
                <p className="text-gray-600">
                  Click "Sync DTDC Tracking" to fetch latest delivery status for all Ashmiaa orders with DTDC tracking numbers (I76854xxx format).
                </p>
              </div>
              <div>
                <p className="font-semibold mb-1">2. DTDC Invoice Import (Shipping Charges):</p>
                <p className="text-gray-600">
                  Upload DTDC invoice PDF from portal (Reports → Invoice). System extracts tracking numbers and shipping charges, then matches to orders automatically.
                </p>
              </div>
              <div>
                <p className="font-semibold mb-1">3. COD Remittance Import:</p>
                <p className="text-gray-600">
                  Upload COD remittance CSV from DTDC portal (Reports → Remittance). Contains CN Number, Order#, COD Amount, UTR/AXISCN for matching.
                </p>
              </div>
              <div>
                <p className="font-semibold mb-1">4. Cost Sheet Import:</p>
                <p className="text-gray-600">
                  Upload CSV/Excel with Order Number/Tracking Number and Cost columns. Updates product costs for profit calculation.
                </p>
              </div>
              <div>
                <p className="font-semibold mb-1">5. Bank Statement Import:</p>
                <p className="text-gray-600">
                  Upload your Ashmiaa bank statement showing COD receipts. System matches by AXISCN references to verify payments received.
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 italic">
                  <strong>Note:</strong> Unmatched records are logged below for review. All features work with Ashmiaa store (DTDC India).
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DTDCManagement;
